import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    ArrowLeft,
    CalendarDays,
    Eye,
    ImagePlus,
    IndianRupee,
    Tag,
    Upload,
} from "lucide-react";

import { Header } from "./Header";
import { MobileHeader } from "./MobileHeader";

import { useCreateOffer } from "../hooks/useCreateOffer";

export function CreateOffer() {

    const navigate = useNavigate();

    const fileInputRef = useRef(null);

    const createOfferMutation =
        useCreateOffer();

    const [successMessage, setSuccessMessage] = useState("");

    const [offer, setOffer] = useState({

        title: "",

        image: null,

        imagePreview: "",

        discountType: "percentage",

        discountValue: "",

        minimumPurchaseAmount: "",

        validFrom: "",

        validTo: "",

    });

    const formDisabled = !!successMessage;

    function updateField(field, value) {

        setOffer(previous => ({

            ...previous,

            [field]: value,

        }));

    }

    function handleImageUpload(event) {

        const file =
            event.target.files[0];

        if (!file)
            return;

        const preview =
            URL.createObjectURL(file);

        setOffer(previous => ({

            ...previous,

            image: file,

            imagePreview: preview,

        }));

    }

    async function handleSubmit(event) {

        event.preventDefault();

        try {

            await createOfferMutation.mutateAsync({

                title: offer.title,

                description: offer.description,

                image: offer.image,

                discountType: offer.discountType,

                discountValue: Number(
                    offer.discountValue
                ),

                minimumPurchaseAmount:
                    offer.minimumPurchaseAmount === ""
                        ? 0
                        : Number(
                            offer.minimumPurchaseAmount
                        ),

                validFrom: offer.validFrom,

                validTo: offer.validTo,

            });

            setSuccessMessage(
                "Offer successfully created. We will distribute it at all relevant checkout moments across our network."
            );

        } catch (error) {
            alert(
                error.response?.data?.error ||
                error.message ||
                "Something went wrong."
            );
            console.error(error);

        }

    }

    const previewDiscount = useMemo(() => {

        if (!offer.discountValue)
            return "--";

        return offer.discountType === "percentage"

            ? `${offer.discountValue}% OFF`

            : `₹${offer.discountValue} OFF`;

    }, [

        offer.discountType,

        offer.discountValue,

    ]);

    return (

        <>
            <div className="create-offer-content">
                {/* <MobileHeader />

                <Header

                    title="Create Offer"

                    subtitle="Create a new offer for your customers."

                /> */}

                <MobileHeader />

                <main className="create-offer-page">

                    <Header

                        title="Create Offer"

                        subtitle="Create a new offer for your customers."

                    />

                    <button
                        className="back-button"
                        onClick={() => navigate(-1)}
                    >

                        <ArrowLeft size={18} />

                        Back

                    </button>

                    {
                        successMessage && (

                            <div className="create-offer-success">

                                <div className="create-offer-success-icon">

                                    ✓

                                </div>

                                <div>

                                    <h3>

                                        Offer Successfully Created

                                    </h3>

                                    <p>

                                        {successMessage}

                                    </p>

                                </div>

                            </div>

                        )
                    }

                    <form
                        className="create-offer-form"
                        onSubmit={handleSubmit}
                    >

                        <section className="offer-editor-card">

                            {/* Left */}

                            <div className="offer-details">

                                <h2>

                                    <Tag size={20} />

                                    Offer Details

                                </h2>

                                <div className="create-offer-form-group">

                                    <label>

                                        Offer Title

                                    </label>

                                    <input

                                        type="text"

                                        className="create-offer-input"

                                        placeholder="Enter offer title"

                                        value={offer.title}

                                        onChange={(event) =>

                                            updateField(

                                                "title",

                                                event.target.value

                                            )

                                        }
                                        disabled={formDisabled}
                                        required

                                    />

                                </div>

                                <div className="create-offer-form-group">

                                    <label>

                                        Offer Image

                                    </label>

                                    <input

                                        ref={fileInputRef}

                                        type="file"

                                        className="create-offer-input display-none"

                                        accept="image/*"

                                        hidden

                                        onChange={handleImageUpload}

                                        disabled={formDisabled}

                                    />

                                    <button

                                        type="button"

                                        className="image-upload"

                                        disabled={formDisabled}

                                        onClick={() =>

                                            fileInputRef.current.click()

                                        }

                                    >

                                        {

                                            offer.imagePreview ?

                                                <img

                                                    src={offer.imagePreview}

                                                    alt="Preview"

                                                />

                                                :

                                                <>

                                                    <Upload size={36} />

                                                    <p>

                                                        Click to upload

                                                    </p>

                                                    <span>

                                                        PNG, JPG or WEBP

                                                    </span>

                                                </>

                                        }

                                    </button>

                                </div>

                                <div className="create-offer-form-group">

                                    <label>

                                        Discount Type

                                    </label>

                                    <select

                                        className="create-offer-input"

                                        value={offer.discountType}

                                        onChange={(event) =>

                                            updateField(

                                                "discountType",

                                                event.target.value

                                            )

                                        }
                                        disabled={formDisabled}

                                    >

                                        <option value="percentage">

                                            Percentage

                                        </option>

                                        <option value="fixed">

                                            Fixed Amount

                                        </option>

                                    </select>

                                </div>

                                <div className="create-offer-form-group">

                                    <label>

                                        Discount Value

                                    </label>

                                    <div className="input-with-suffix">

                                        <input

                                            type="number"

                                            className="create-offer-input"

                                            min="1"

                                            value={offer.discountValue}

                                            onChange={(event) =>

                                                updateField(

                                                    "discountValue",

                                                    event.target.value

                                                )

                                            }
                                            disabled={formDisabled}
                                            required

                                        />

                                        <span>

                                            {

                                                offer.discountType === "percentage"

                                                    ? "%"

                                                    : <IndianRupee size={16} />

                                            }

                                        </span>

                                    </div>

                                </div>

                                <div className="create-offer-form-group">

                                    <label>

                                        Minimum Purchase Amount

                                    </label>

                                    <div className="input-with-prefix">

                                        <span>

                                            <IndianRupee size={16} />

                                        </span>

                                        <input

                                            type="number"

                                            className="create-offer-input"

                                            min="0"

                                            placeholder="Optional"

                                            value={offer.minimumPurchaseAmount}

                                            onChange={(event) =>

                                                updateField(

                                                    "minimumPurchaseAmount",

                                                    event.target.value

                                                )

                                            }
                                            disabled={formDisabled}
                                        />

                                    </div>

                                </div>

                            </div>

                            {/* Middle */}

                            <div className="offer-validity">

                                <h2>

                                    <CalendarDays size={20} />

                                    Validity

                                </h2>

                                <div className="create-offer-form-group">

                                    <label>

                                        Valid From

                                    </label>

                                    <input

                                        type="datetime-local"

                                        className="create-offer-input"

                                        value={offer.validFrom}

                                        onChange={(event) =>

                                            updateField(

                                                "validFrom",

                                                event.target.value

                                            )

                                        }
                                        disabled={formDisabled}
                                        required

                                    />

                                </div>

                                <div className="create-offer-form-group">

                                    <label>

                                        Valid To

                                    </label>

                                    <input

                                        type="datetime-local"

                                        className="create-offer-input"

                                        value={offer.validTo}

                                        onChange={(event) =>

                                            updateField(

                                                "validTo",

                                                event.target.value

                                            )

                                        }
                                        disabled={formDisabled}
                                        required

                                    />

                                </div>

                            </div>

                            {/* Right */}

                            <aside className="offer-preview">

                                <h2>

                                    <Eye size={20} />

                                    Offer Preview

                                </h2>

                                <div className="preview-card">

                                    <div className="preview-image">

                                        {

                                            offer.imagePreview ?

                                                <img

                                                    src={offer.imagePreview}

                                                    alt="Offer"

                                                />

                                                :

                                                <div className="preview-placeholder">

                                                    <ImagePlus size={48} />

                                                </div>

                                        }

                                    </div>

                                    <div className="preview-content">

                                        <h3>

                                            {

                                                offer.title ||

                                                "Special Offer"

                                            }

                                        </h3>

                                        <div className="preview-badge">

                                            {

                                                previewDiscount

                                            }

                                        </div>

                                        {

                                            offer.minimumPurchaseAmount && (

                                                <p>

                                                    On orders above ₹

                                                    {

                                                        offer.minimumPurchaseAmount

                                                    }

                                                </p>

                                            )

                                        }

                                        <div className="preview-divider" />

                                        {

                                            offer.validFrom && (

                                                <p>

                                                    Starts

                                                    {" "}

                                                    {

                                                        new Date(

                                                            offer.validFrom

                                                        ).toLocaleString()

                                                    }

                                                </p>

                                            )

                                        }

                                        {

                                            offer.validTo && (

                                                <p>

                                                    Ends

                                                    {" "}

                                                    {

                                                        new Date(

                                                            offer.validTo

                                                        ).toLocaleString()

                                                    }

                                                </p>

                                            )

                                        }

                                    </div>

                                </div>

                            </aside>

                        </section>

                        <div className="create-offer-footer">

                            {/* <button

                                type="button"

                                className="secondary-button"

                                onClick={() =>

                                    navigate(-1)

                                }

                            >

                                Cancel

                            </button> */}

                            <button

                                type="submit"

                                className="primary-button"

                                disabled={

                                    createOfferMutation.isPending ||
                                    formDisabled

                                }

                            >

                                {
                                    createOfferMutation.isPending
                                        ? "Creating..."
                                        : formDisabled
                                            ? "Offer Created"
                                            : "Create Offer"
                                }

                            </button>

                        </div>

                    </form>

                </main>
            </div>

        </>

    );

}